/**
 * Price resolution for storefront product cards.
 *
 * The schema allows a sub-variant's `price` to be null, meaning "inherit the
 * parent main variant's defaultPrice" (the terminal, non-null fallback). Some
 * endpoints (e.g. recommendations) return that raw shape, so a card that only
 * reads `variant.price` shows ₪0. These helpers resolve the *effective* price
 * across the shapes we receive:
 *   - serialized card variant:        { price: 100 }
 *   - raw inherited variant:          { price: null, mainVariant: { defaultPrice: 100 } }
 *   - serialized card (no variants):  { priceFrom: 100, priceTo: 140 }
 */

function num(x) {
  if (x === null || x === undefined || x === '') return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/**
 * Effective price of a single variant. Its own price wins (including an
 * explicit 0 = free); otherwise fall back to the main variant's defaultPrice,
 * then a couple of legacy field names. Returns a number or null (no price).
 */
export function effectiveVariantPrice(variant) {
  if (!variant) return null;
  return (
    num(variant.price)
    ?? num(variant.mainVariant && variant.mainVariant.defaultPrice)
    ?? num(variant.defaultPrice)
    ?? null
  );
}

/**
 * A DISCOUNT A SHOPPER CAN BELIEVE — the percentage off, or null for no badge.
 *
 * A discount is a difference FROM a price, so it needs a price to be off. A
 * card whose price resolved to 0, or to nothing at all, has no difference to
 * state — only an absurdity. dk-store's ‏"צמיד גבר LORENO LIM"‎ arrived from the
 * WooCommerce importer priced at 0 against a compare-at of ₪279 and every card
 * that read it offered "−100%": a bracelet advertised as free.
 *
 * Whatever put the zero there — a bad import, an inherit that did not resolve,
 * a campaign that ran past the price — the card is the last place that can
 * refuse, and refusing is: NO badge, no strike-through, no "save ₪279". A
 * missing badge is a shrug; a −100% badge is a promise the checkout will not
 * keep. Anything that rounds to a hundred is the same lie one agora short.
 */
export function discountPercent(price, compareAt) {
  const p = num(price);
  const c = num(compareAt);
  if (p == null || c == null) return null;
  if (p <= 0 || c <= 0) return null;
  if (c <= p) return null;
  const pct = Math.round(((c - p) / c) * 100);
  return pct > 0 && pct < 100 ? pct : null;
}

/**
 * A PRICE A DISCOUNT TOOK TO NOTHING — true when something was LISTED (above 0)
 * and the discount took all of it.
 *
 * `discountPercent` above refuses a percentage off a zero, and it must: a
 * bracelet imported at 0 is a data error, whatever compare-at stands beside it.
 * But a ₪30 strap that "₪30 off this strap" brings to 0 is charged 0 — it is
 * FREE, and a card that prints a bare "0 ₪" beside it, with no word of why,
 * reads as the same error. The two zeros differ in one fact: whether anything
 * was listed. Say "free" for this one; never a "−100%" badge for either.
 */
export function discountMadeItFree(price, listPrice) {
  const p = num(price);
  const l = num(listPrice);
  return p != null && l != null && l > 0 && p <= 0;
}

/** Effective compare-at price of a variant (same inheritance rule). */
export function effectiveVariantCompareAt(variant) {
  if (!variant) return null;
  return (
    num(variant.compareAtPrice)
    ?? num(variant.mainVariant && variant.mainVariant.defaultCompareAtPrice)
    ?? null
  );
}

/**
 * Cheapest variant that actually has a resolvable price, plus that price.
 * Variants with no price are skipped so a price-less option never drags the
 * card down to ₪0.
 */
export function cheapestPricedVariant(variants) {
  const list = Array.isArray(variants) ? variants : [];
  let best = null;
  let bestPrice = null;
  for (const v of list) {
    const p = effectiveVariantPrice(v);
    if (p == null) continue;
    if (bestPrice == null || p < bestPrice) { best = v; bestPrice = p; }
  }
  return { variant: best, price: bestPrice };
}

/** Whether the resolvable variant prices differ (drives a "from" label). */
export function variantPricesVary(variants) {
  const prices = (Array.isArray(variants) ? variants : [])
    .map(effectiveVariantPrice)
    .filter((p) => p != null);
  return prices.length > 1 && prices.some((p) => p !== prices[0]);
}

/**
 * Display price for a product card. Prefers the cheapest priced variant; falls
 * back to a serialized card's priceFrom/priceTo. Returns:
 *   { variant, price (number|null), varies (bool) }
 * `variant` is always a real variant when any exist (so quick-add still works),
 * even if its price had to come from priceFrom.
 */
export function productCardPrice(product) {
  const variants = (product && Array.isArray(product.variants)) ? product.variants : [];
  const { variant, price } = cheapestPricedVariant(variants);
  if (price != null) {
    return { variant, price, varies: variantPricesVary(variants) };
  }
  const from = num(product && product.priceFrom);
  if (from != null) {
    const to = num(product && product.priceTo);
    return { variant: variants[0] || null, price: from, varies: to != null && to !== from };
  }
  return { variant: variants[0] || null, price: null, varies: false };
}
